import React, { useEffect, useRef, useState } from "react"
import Collapsible from "@/components/core/Collapsible"
import {
  sanitizeInnerHtml,
  getShortenedUrlwithGeneral,
} from "@/utils/helper"
import arrowUp from "@/public/icons/arrow-up.svg"
import arrowDown from "@/public/icons/arrow-down.svg"
import { getConfig } from "@/constants/config"

const getStableAccordionId = (prefix, value) =>
  `${prefix}-${value}`.replace(/[^a-zA-Z0-9_-]/g, "-")

const Accordion = props => {
  const {
    isOpen = false,
    contentOverflow = false,
    trigger = false,
    handleOpen = () => {},
    handleClose = () => {},
    containerElementProps,
    isArrow = false,
    items,
    itemsOrder,
    id,
    titleKey,
    componentPosition,
    componentLinkType,
    accordionProperties: { expandedItems } = {},
  } = props

  const getDefaultOpenItems = () =>
    Array.isArray(expandedItems)
      ? expandedItems
      : isOpen
      ? [...(itemsOrder || [])]
      : []
  const [openItems, setOpenItems] = useState(getDefaultOpenItems)
  const [general, setGeneral] = useState(null)
  const accordiumObject = useRef(null)
  const pageType = window.eventPageType

  useEffect(() => {
    getConfig().then((config = {}) => {
      setGeneral(config?.general)
    })
  }, [])

  useEffect(() => {
    setOpenItems(getDefaultOpenItems())
  }, [expandedItems, isOpen, itemsOrder])

  const CollapsibleHeader = ({ title, open = false }) => (
    <h2 className="cmp-accordion__header" aria-label={title}>
      <span
        className={`cmp-accordion__button ${
          open ? "cmp-accordion__button--expanded" : ""
        }`}
        data-gbh-data-layer={`{"clickInternalLinks":"true","internalLinkPosition":"${
          componentPosition ? componentPosition : "accordion"
        }",
        "internalLinkZoneName":"n/a","internalLinkURL":"n/a","internalLinkName":"${title?.toLowerCase()}",
        "eventName":"${pageType?.toLowerCase()}:${title?.toLowerCase()}",
        "internalLinkType":"${pageType?.toLowerCase()}:${
          componentLinkType ? componentLinkType : "navigation"
        }",
        "eventType":"accordion",
        "eventAction":"${pageType?.toLowerCase()}:${title?.toLowerCase()}"}`}
      >
        <span className="cmp-accordion__title">
          {title}
        </span>
        {!isArrow ? (
          <span className="cmp-accordion__icon" aria-hidden>
            <div className="line line-1"></div>
            <div className="line line-2"></div>
          </span>
        ) : (
          <span className="cmp-accordion__icon" aria-hidden>
            <div className="down-arrow">
              <img src={arrowDown.src} alt={"Arrow Down"} />
            </div>
            <div className="up-arrow">
              <img src={arrowUp.src} alt={"Arrow Up"} />
            </div>
          </span>
        )}
      </span>
    </h2>
  )

  const CollapsibleInnerText = ({
    gridColumnClassName,
    faqText,
    isExpanded,
    title,
  }) => {
    const finalText = sanitizeInnerHtml(
      getShortenedUrlwithGeneral(faqText, general)
    )
    return (
      <div
        data-accordian-title={title}
        className={`cmp-accordion__panel${
          isExpanded
            ? " cmp-accordion__panel--expanded"
            : " cmp-accordion__panel--hidden"
        }`}
      >
        <div className={`text ${gridColumnClassName}`}>
          <div className="cmp-text">{finalText}</div>
        </div>
      </div>
    )
  }

  return (
    <div
      id={id}
      ref={accordiumObject}
      className="cmp-accordion"
      data-placeholder-text="false"
    >
      {itemsOrder?.map(item => {
        const title = items[item][titleKey]
        const itemId = getStableAccordionId("accordion-item", `${id}-${item}`)
        const triggerId = getStableAccordionId(
          "collapsible-trigger",
          `${id}-${item}`
        )
        const contentId = getStableAccordionId(
          "collapsible-content",
          `${id}-${item}`
        )
        let gridColumnClassName = items[item]["appliedCssClassNames"]
        let faqText = items[item]["text"]

        if (!faqText) {
          faqText = ""
          const itemsOrderArr = items[item][":itemsOrder"]
          if (itemsOrderArr) {
            itemsOrderArr.forEach(itemTx => {
              faqText = `${faqText}${items[item][":items"][itemTx]["text"]}`
              gridColumnClassName = `${items[item][":items"][itemTx]["appliedCssClassNames"]}`
            })
          }
        }

        const isItemExpanded = openItems.includes(item)

        return (
          <React.Fragment key={itemId}>
            <div
              data-cmp-hook-accordion="item"
              className="cmp-accordion__item"
              id={itemId}
              data-cmp-data-layer={`{"${itemId}":{"@type":"kohler-us/components/accordion/item","dc:title":"${title}"}}`}
            >
              <Collapsible
                open={isItemExpanded}
                onTriggerOpening={() => {
                  setOpenItems(prevItems =>
                    prevItems.includes(item) ? prevItems : [...prevItems, item]
                  )
                  handleOpen(item)
                }}
                onTriggerClosing={() => {
                  setOpenItems(prevItems =>
                    prevItems.filter(prevItem => prevItem !== item)
                  )
                  handleClose(item)
                }}
                containerElementProps={containerElementProps}
                overflowWhenOpen={contentOverflow ? contentOverflow : "hidden"}
                openedClassName="collapsible--open"
                triggerOpenedClassName="collapsible__trigger--open"
                trigger={
                  trigger ? trigger : <CollapsibleHeader title={title} />
                }
                triggerWhenOpen={
                  trigger ? (
                    trigger
                  ) : (
                    <CollapsibleHeader title={title} open={true} />
                  )
                }
                transitionTime={400}
                easing="ease-in-out"
                tabIndex="0"
                triggerElementProps={{
                  id: triggerId,
                }}
                contentElementId={contentId}
              >
                <CollapsibleInnerText
                  gridColumnClassName={gridColumnClassName}
                  faqText={faqText}
                  isExpanded={isItemExpanded}
                  title={title}
                />
              </Collapsible>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default Accordion
